import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Chip,
  Stack,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  FitnessCenter as CoachingIcon,
  Home as HomeIcon,
  AccessTime as AccessTimeIcon,
  LocationOn as LocationOnIcon,
  People as PeopleIcon,
  AttachMoney as AttachMoneyIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  CalendarToday as CalendarTodayIcon,
  DateRange as DateRangeIcon,
  Group as GroupIcon,
  ManageAccounts as ManageAccountsIcon
} from '@mui/icons-material';
import { coachingProgramAPI, coachingProgramInstanceAPI, amenityAPI } from '../../services/api';
import { CoachingProgram, CoachingProgramEnrollment } from '../../types/coaching';
import { CoachingProgramInstance } from '../../types';
import { formatDate, formatTimeString } from '../../utils/dateUtils';
import { useAuth } from '../../context/AuthContext';
import { useMemberContext } from '../../hooks/useMemberContext';
import EnrollMemberToProgramDialog from './EnrollMemberToProgramDialog';

const ViewCoachingProgramPage: React.FC = () => {
  const { programId } = useParams<{ programId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { memberProfile } = useMemberContext();
  
  const [programData, setProgramData] = useState<CoachingProgram | null>(null);
  const [enrollments, setEnrollments] = useState<CoachingProgramEnrollment[]>([]);
  const [programInstances, setProgramInstances] = useState<CoachingProgramInstance[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [enrollmentsLoading, setEnrollmentsLoading] = useState<boolean>(false);
  const [instancesLoading, setInstancesLoading] = useState<boolean>(false);
  const [openEnrollDialog, setOpenEnrollDialog] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [venueNames, setVenueNames] = useState<Map<string, string>>(new Map());
  
  // Member enrollment tracking
  const [memberEnrollment, setMemberEnrollment] = useState<CoachingProgramEnrollment | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState<boolean>(false);
  const [cancelLoading, setCancelLoading] = useState<boolean>(false);
  const [cancellationSuccess, setCancellationSuccess] = useState<boolean>(false);

  useEffect(() => {
    if (programId) {
      fetchProgramData();
      fetchAmenities();
    }
  }, [programId]);

  const fetchProgramData = async (skipLoadingState: boolean = false) => {
    if (!programId) return;

    if (!skipLoadingState) {
      setLoading(true);
    }
    setError(null);

    try {
      const response = await coachingProgramAPI.getCoachingProgramById(programId);
      setProgramData(response.data);
      
      // Fetch enrollments for all users to check if member is enrolled
      // Admin/coach can see all enrollments, members can check their own
      fetchEnrollments();
      
      // Also fetch instances if user has appropriate permissions
      if (user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'coach') {
        fetchProgramInstances();
      }
    } catch (error: any) {
      console.error('Error fetching coaching program:', error);
      setError(error.response?.data?.message || 'Failed to fetch coaching program details');
    } finally {
      if (!skipLoadingState) {
        setLoading(false);
      }
    }
  };

  const fetchAmenities = async () => {
    try {
      const response = await amenityAPI.getAmenities();
      const names = new Map<string, string>();
      response.data.forEach((amenity: any) => {
        names.set(amenity.id, amenity.name);
      });
      setVenueNames(names);
    } catch (error: any) {
      console.error('Error fetching amenities:', error);
      // Don't show error - not critical
    }
  };

  const fetchProgramInstances = async () => {
    if (!programId) return;

    setInstancesLoading(true);
    try {
      const response = await coachingProgramInstanceAPI.getInstances({
        parentProgramId: programId,
        includeEnrollments: true
      });
      setProgramInstances(response.data || []);
    } catch (error: any) {
      console.error('Error fetching program instances:', error);
      // Don't set main error state for instances fetch failures
    } finally {
      setInstancesLoading(false);
    }
  };

  const fetchEnrollments = async () => {
    if (!programId) return;

    setEnrollmentsLoading(true);
    try {
      const response = await coachingProgramAPI.getCoachingProgramEnrollments(programId);
      const fetchedEnrollments = response.data || [];
      setEnrollments(fetchedEnrollments);
      
      // Check if current member profile is enrolled
      if (memberProfile?.id) {
        const myEnrollment = fetchedEnrollments.find(
          (e: CoachingProgramEnrollment) => e.memberId === memberProfile.id && e.status === 'ENROLLED'
        );
        setMemberEnrollment(myEnrollment || null);
      }
    } catch (error: any) {
      console.error('Error fetching enrollments:', error);
      // Don't show enrollment errors as main error - they might not have permission
    } finally {
      setEnrollmentsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return 'success';
      case 'UPCOMING':
        return 'info';
      case 'COMPLETED':
        return 'default';
      case 'CANCELLED':
        return 'error';
      default:
        return 'default';
    }
  };

  const getEnrollmentStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'ENROLLED':
        return 'success';
      case 'WAITLISTED':
        return 'warning';
      case 'CANCELLED':
        return 'error';
      case 'COMPLETED':
        return 'info';
      default:
        return 'default';
    }
  };

  const canEditProgram = () => {
    return user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'coach';
  };

  const handleEditProgram = () => {
    // Navigate to dedicated edit page
    navigate(`/coaching/programs/edit/${programId}`);
  };

  const handleEnrollMember = () => {
    setOpenEnrollDialog(true);
  };

  const handleCancelEnrollment = () => {
    setCancelDialogOpen(true);
  };

  const handleConfirmCancellation = async () => {
    if (!programId) return;

    setCancelLoading(true);
    setError(null);

    try {
      const response = await coachingProgramAPI.cancelOwnEnrollment(programId);
      
      setCancellationSuccess(true);
      setCancelDialogOpen(false);
      setMemberEnrollment(null);

      // Refresh program data
      await fetchProgramData(true);

      // Show success message
      setSuccessMessage(response.data.message || 'Enrollment cancelled successfully');

      // Redirect to dashboard after 3 seconds
      setTimeout(() => {
        navigate('/dashboard', { state: { from: 'coaching-program' } });
      }, 3000);
    } catch (error: any) {
      console.error('Error cancelling enrollment:', error);
      setError(error.response?.data?.message || 'Failed to cancel enrollment');
      setCancelDialogOpen(false);
    } finally {
      setCancelLoading(false);
    }
  };

  const handleCloseCancelDialog = () => {
    setCancelDialogOpen(false);
  };

  const formatDaysOfWeek = (daysOfWeek: number[] | undefined) => {
    if (!daysOfWeek || daysOfWeek.length === 0) return 'None specified';
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return daysOfWeek.map(day => dayNames[day]).join(', ');
  };

  const getVenueNames = (venueIds?: string[]): string => {
    if (!venueIds || venueIds.length === 0) return 'No venue';
    return venueIds
      .map(id => venueNames.get(id) || id)
      .join(', ');
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4 }}>
          <Alert severity="error">{error}</Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ mt: 2 }}
          >
            Go Back
          </Button>
        </Box>
      </Container>
    );
  }

  if (!programData) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4 }}>
          <Alert severity="warning">Coaching program not found</Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ mt: 2 }}
          >
            Go Back
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Button
            component={RouterLink}
            to="/dashboard"
            color="inherit"
            startIcon={<HomeIcon />}
            sx={{ textTransform: 'none' }}
          >
            Dashboard
          </Button>
          <Button
            component={RouterLink}
            to="/calendar"
            color="inherit"
            startIcon={<CalendarTodayIcon />}
            sx={{ textTransform: 'none' }}
          >
            Calendar
          </Button>
          <Typography color="text.primary">Coaching Program Details</Typography>
        </Breadcrumbs>

        {/* Success Message */}
        {successMessage && (
          <Alert 
            severity="success" 
            icon={<ArrowBackIcon />}
            sx={{ mb: 3 }}
          >
            {successMessage}
          </Alert>
        )}

        {/* Cancellation Success Message */}
        {cancellationSuccess && (
          <Alert 
            severity="info" 
            sx={{ mb: 3 }}
          >
            Enrollment cancelled successfully. Redirecting to dashboard...
          </Alert>
        )}

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
              <CoachingIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
              {programData.name}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {programData.description}
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              variant="outlined"
              disabled={cancelLoading}
            >
              Back
            </Button>
            {memberEnrollment && !canEditProgram() && (
              <Button
                variant="contained"
                color="error"
                onClick={handleCancelEnrollment}
                disabled={cancelLoading || cancellationSuccess}
              >
                {cancelLoading ? 'Cancelling...' : 'Cancel'}
              </Button>
            )}
            {canEditProgram() && (
              <Button
                startIcon={<EditIcon />}
                onClick={handleEditProgram}
                variant="contained"
              >
                Edit Program
              </Button>
            )}
          </Stack>
        </Box>

        <Grid container spacing={3}>
          {/* Main Program Information */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Program Information
              </Typography>
              
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <List disablePadding>
                    {/* Program Duration & Session Time - Combined */}
                    <ListItem sx={{ px: 0, py: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 3 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', flex: { xs: '1 1 auto', sm: '0 0 50%' } }}>
                          <DateRangeIcon sx={{ mr: 2, color: 'primary.main', fontSize: 20 }} />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>Program Duration</Typography>
                            <Typography variant="body2" color="text.secondary">{formatDate(programData.startDate)} - {formatDate(programData.endDate)}</Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', flex: { xs: '1 1 auto', sm: '0 0 50%' } }}>
                          <AccessTimeIcon sx={{ mr: 2, color: 'primary.main', fontSize: 20 }} />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>Session Time</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {formatTimeString(programData.sessionTime || programData.recurringPattern?.startTime || '')} ({programData.sessionDurationMinutes || programData.recurringPattern?.duration || 60} min)
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </ListItem>

                    {/* Coach & Type/Level - Combined */}
                    <ListItem sx={{ px: 0, py: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 3 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', flex: { xs: '1 1 auto', sm: '0 0 50%' } }}>
                          <PersonIcon sx={{ mr: 2, color: 'primary.main', fontSize: 20 }} />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>Coach</Typography>
                            <Typography variant="body2" color="text.secondary">{programData.coachName || 'Not assigned'}</Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', flex: { xs: '1 1 auto', sm: '0 0 50%' } }}>
                          <CoachingIcon sx={{ mr: 2, color: 'primary.main', fontSize: 20 }} />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>Type & Level</Typography>
                            <Typography variant="body2" color="text.secondary">{programData.type} - {programData.level}</Typography>
                          </Box>
                        </Box>
                      </Box>
                    </ListItem>

                    {/* Venue */}
                    <ListItem sx={{ px: 0, py: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <LocationOnIcon sx={{ mr: 2, color: 'primary.main', fontSize: 20 }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>Venue</Typography>
                          <Typography variant="body2" color="text.secondary">{programData.venues && programData.venues.length > 0 ? getVenueNames(programData.venues) : 'Not specified'}</Typography>
                        </Box>
                      </Box>
                    </ListItem>

                    {/* Capacity */}
                    <ListItem sx={{ px: 0, py: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <GroupIcon sx={{ mr: 2, color: 'primary.main', fontSize: 20 }} />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>Capacity</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {programData.currentEnrollments || 0} / {programData.capacity} enrolled
                            </Typography>
                          </Box>
                          <Chip
                            size="small"
                            label={(programData.currentEnrollments || 0) >= programData.capacity ? 'Full' : 'Available'}
                            color={(programData.currentEnrollments || 0) >= programData.capacity ? 'error' : 'success'}
                            variant="outlined"
                          />
                        </Box>
                      </Box>
                    </ListItem>
                  </List>
                </Grid>
              </Grid>
              
              {(programData.totalCost || programData.dropInCost) && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    {programData.totalCost && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <AttachMoneyIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body1">
                          <strong>Program Cost:</strong> ${programData.totalCost}
                        </Typography>
                      </Box>
                    )}
                    {programData.dropInCost && programData.allowDropIns && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <AttachMoneyIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body1">
                          <strong>Drop-in Cost:</strong> ${programData.dropInCost}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </>
              )}

              {programData.recurringPattern && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      <ScheduleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Schedule Pattern
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {programData.recurringPattern.frequency.charAt(0).toUpperCase() + programData.recurringPattern.frequency.slice(1)}
                      {programData.recurringPattern.interval > 1 && ` (every ${programData.recurringPattern.interval} ${programData.recurringPattern.frequency}s)`}
                      {programData.recurringPattern.daysOfWeek && programData.recurringPattern.daysOfWeek.length > 0 && (
                        <span>
                          {' - '}
                          {formatDaysOfWeek(programData.recurringPattern.daysOfWeek)}
                        </span>
                      )}
                    </Typography>
                  </Box>
                </>
              )}

              {programData.enrollmentDeadline && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Enrollment Deadline
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(programData.enrollmentDeadline)}
                    </Typography>
                  </Box>
                </>
              )}

              {programData.notes && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Notes
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {programData.notes}
                    </Typography>
                  </Box>
                </>
              )}
            </Paper>

            {/* Program Instances Section (for admin/coach users) */}
            {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'coach') && (
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Program Instances ({programInstances.length})
                </Typography>
                {instancesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : programInstances.length > 0 ? (
                  <Grid container spacing={2}>
                    {programInstances.map((instance) => (
                      <Grid size={{ xs: 12, md: 6 }} key={instance.id}>
                        <Card variant="outlined">
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                              <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                  {formatDate(instance.sessionDate)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {formatTimeString(instance.startTime)} - {formatTimeString(instance.endTime)}
                                </Typography>
                              </Box>
                              <Chip
                                label={instance.status}
                                color={getStatusColor(instance.status)}
                                size="small"
                              />
                            </Box>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <LocationOnIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                              <Typography variant="body2" color="text.secondary">
                                {getVenueNames(instance.venues)}
                              </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <PersonIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                              <Typography variant="body2" color="text.secondary">
                                {instance.coachName}
                              </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <PeopleIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                              <Typography variant="body2" color="text.secondary">
                                {instance.enrolledCount || 0} / {instance.capacity} enrolled
                              </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => navigate(`/coaching/program-instances/${instance.id}`)}
                              >
                                View Details
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<ManageAccountsIcon />}
                                onClick={() => navigate(`/coaching/program-instances/${instance.id}`)}
                              >
                                Manage Enrollments
                              </Button>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {programData?.recurringPattern ? 'No instances generated yet.' : 'This program has no scheduled instances.'}
                  </Typography>
                )}
              </Paper>
            )}

            {/* Enrollments Section (for admin/coach users) */}
            {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'coach') && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Program Enrollments ({enrollments.length})
                </Typography>
                {enrollmentsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : enrollments.length > 0 ? (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Member</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Sessions Attended</TableCell>
                          <TableCell>Enrolled At</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {enrollments.map((enrollment) => (
                          <TableRow key={enrollment.id}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                                  {enrollment.isGuestEnrollment 
                                    ? (enrollment.guestName?.charAt(0) || 'G')
                                    : (enrollment.memberName?.charAt(0) || enrollment.memberEmail?.charAt(0))
                                  }
                                </Avatar>
                                {enrollment.isGuestEnrollment 
                                  ? (enrollment.guestName || 'Guest')
                                  : (enrollment.memberName || 'Unknown')
                                }
                                {enrollment.isGuestEnrollment && (
                                  <Chip label="Guest" size="small" sx={{ ml: 1 }} />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              {enrollment.isGuestEnrollment 
                                ? enrollment.guestEmail 
                                : enrollment.memberEmail
                              }
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={enrollment.enrollmentType === 'FULL_PROGRAM' ? 'Full Program' : 'Drop-in'}
                                color={enrollment.enrollmentType === 'FULL_PROGRAM' ? 'primary' : 'default'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={enrollment.status}
                                color={getEnrollmentStatusColor(enrollment.status)}
                                size="small"
                              />
                              {enrollment.waitlistPosition && (
                                <Typography variant="caption" display="block">
                                  Position: #{enrollment.waitlistPosition}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>{enrollment.sessionsAttended}</TableCell>
                            <TableCell>
                              {new Date(enrollment.createdAt).toLocaleDateString()} {new Date(enrollment.createdAt).toLocaleTimeString()}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No enrollments for this program yet.
                  </Typography>
                )}
              </Paper>
            )}
          </Grid>

          {/* Sidebar */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Stack spacing={3}>
              {/* Status Card */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Status
                  </Typography>
                  <Chip
                    label={programData.status}
                    color={getStatusColor(programData.status)}
                    sx={{ mb: 2 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Active: {programData.active ? 'Yes' : 'No'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Drop-ins: {programData.allowDropIns ? 'Allowed' : 'Not allowed'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Created: {new Date(programData.createdAt).toLocaleDateString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Updated: {new Date(programData.updatedAt).toLocaleDateString()}
                  </Typography>
                </CardContent>
              </Card>

              {/* Enrollment Status Card - Only for admin/coach */}
              {canEditProgram() && (
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Enrollment Status
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="h3" color="primary">
                        {programData.currentEnrollments || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        of {programData.capacity} spots filled
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">
                        Available: {(programData.capacity - (programData.currentEnrollments || 0))}
                      </Typography>
                      <Typography variant="body2">
                        {Math.round(((programData.currentEnrollments || 0) / programData.capacity) * 100)}% full
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: '100%',
                        height: 8,
                        backgroundColor: 'grey.300',
                        borderRadius: 1,
                        overflow: 'hidden'
                      }}
                    >
                      <Box
                        sx={{
                          width: `${Math.min(((programData.currentEnrollments || 0) / programData.capacity) * 100, 100)}%`,
                          height: '100%',
                          backgroundColor: ((programData.currentEnrollments || 0) / programData.capacity) >= 0.8 ? 'error.main' : 'primary.main',
                          transition: 'width 0.3s ease'
                        }}
                      />
                    </Box>
                    {programData.waitlistCount > 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Waitlist: {programData.waitlistCount} people
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Quick Actions */}
              {canEditProgram() && (
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Quick Actions
                    </Typography>
                    <Stack spacing={1}>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={handleEditProgram}
                      >
                        Edit Program
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<PeopleIcon />}
                        onClick={handleEnrollMember}
                      >
                        Enroll Member
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Box>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success">
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Enroll Member to Program Dialog */}
      <EnrollMemberToProgramDialog
        open={openEnrollDialog}
        onClose={() => setOpenEnrollDialog(false)}
        program={programData}
        onSuccess={async (message) => {
          setSuccessMessage(message || 'Member enrolled successfully!');
          // Refresh program data to update enrollment counts - skip loading state to avoid flicker
          await fetchProgramData(true);
        }}
      />

      {/* Cancel Enrollment Confirmation Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={handleCloseCancelDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Cancel Enrollment
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom sx={{ mt: 2 }}>
            Are you sure you want to cancel your enrollment in this coaching program?
          </Typography>
          
          {programData && (
            <Box sx={{ mt: 3 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Important:</strong> You can only cancel enrollment before the program starts and at least 24 hours in advance.
              </Alert>

              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>Program:</strong> {programData.name}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Start Date:</strong> {formatDate(programData.startDate)}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>End Date:</strong> {formatDate(programData.endDate)}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Coach:</strong> {programData.coachName}
                </Typography>
              </Box>

              <Alert severity="info" sx={{ mt: 2 }}>
                If you are cancelling within 24 hours of the program start or after the program has begun, please contact the program instructor/coach directly.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCancelDialog} disabled={cancelLoading}>
            Keep Enrollment
          </Button>
          <Button
            onClick={handleConfirmCancellation}
            variant="contained"
            color="error"
            disabled={cancelLoading}
            startIcon={cancelLoading ? <CircularProgress size={20} /> : null}
          >
            {cancelLoading ? 'Cancelling...' : 'Confirm Cancellation'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ViewCoachingProgramPage;